import { useCallback, useMemo } from 'react';
import I18n from 'I18n';
import { Select } from 'foundations-components/transitional/components/Select';
import { Text } from 'foundations-components/components/Text';
import Skeleton from 'personalization-lib/components/shared/display/Skeleton';
import ManageVariantsLink from 'personalization-lib/components/shared/links/ManageVariantsLink';
import VariantSelectValue from 'personalization-lib/components/variantSelect/VariantSelectValue';
import VariantSelectOption from 'personalization-lib/components/variantSelect/VariantSelectOption';
import withInlineErrorHandling from 'personalization-lib/components/shared/hocs/withInlineErrorHandling';
import useVariants from 'personalization-lib/hooks/variants/useVariants';
import useCanViewPersonalizations from 'personalization-lib/hooks/auth/useCanViewPersonalizations';
import useButtonClickTracker from 'personalization-lib/hooks/tracking/library/useButtonClickTracker';
import { getVariantName } from 'personalization-lib/utils/variantSelect';
import { getIsInIframe } from 'personalization-lib/utils/location';
import { jsx as _jsx } from "react/jsx-runtime";
const VariantSelect = ({
  value,
  strategyId,
  onChange,
  onError,
  'data-onboarding-target': onboardingTarget,
  actionSource
}) => {
  const isInIFrame = getIsInIframe();
  const hasPersonalizationAccess = useCanViewPersonalizations();
  const {
    variants,
    loading,
    error
  } = useVariants(strategyId, onError);
  const trackButtonClick = useButtonClickTracker({
    actionSource
  });
  const options = useMemo(() => variants.filter(variant => variant.assetData).map(variant => Object.assign({
    value: variant.assetCoordinates.id,
    text: getVariantName(variant.assetData),
    loading,
    error
  }, variant)), [variants, loading, error]);
  const handleChange = useCallback(e => {
    trackButtonClick.variantSelect();
    onChange(e.target.value);
  }, [onChange, trackButtonClick]);
  if (!hasPersonalizationAccess) {
    return null;
  }
  if (isInIFrame) {
    return /*#__PURE__*/_jsx(ManageVariantsLink, {
      strategyId: strategyId
    });
  }
  if (loading) {
    return /*#__PURE__*/_jsx(Select, {
      "data-test-id": "variant-select",
      minInlineSize: "200px",
      menuWidth: 400,
      buttonUse: "tertiary-light",
      buttonSize: "small",
      disabled: true,
      valueRenderer: () => /*#__PURE__*/_jsx(Skeleton, {
        blockSize: "1em",
        borderRadius: "400",
        use: "on-dark"
      })
    });
  }
  return /*#__PURE__*/_jsx(Select, {
    "data-test-id": "variant-select",
    "data-onboarding-target": onboardingTarget !== null && onboardingTarget !== void 0 ? onboardingTarget : 'variant-select',
    buttonUse: "tertiary-light",
    buttonSize: "small",
    menuWidth: 400,
    maxInlineSize: "280px",
    value: value,
    valueComponent: VariantSelectValue,
    error: Boolean(error),
    disabled: Boolean(error),
    dropdownFooter: /*#__PURE__*/_jsx(ManageVariantsLink, {
      strategyId: strategyId
    }),
    placeholder: I18n.text('personalizationLib.variantSelect.placeholder'),
    onChange: handleChange,
    optionComponent: VariantSelectOption,
    options: options
  });
};
const VariantSelectErrorState = () => /*#__PURE__*/_jsx(Select, {
  "data-test-id": "variant-select-error",
  minInlineSize: "200px",
  menuWidth: 400,
  buttonUse: "tertiary-light",
  buttonSize: "small",
  disabled: true,
  error: true,
  valueRenderer: () => /*#__PURE__*/_jsx(Text, {
    use: "error",
    as: "span",
    children: I18n.text('personalizationLib.variantSelect.error')
  })
});
export default withInlineErrorHandling(VariantSelect, {
  fallback: /*#__PURE__*/_jsx(VariantSelectErrorState, {})
});